import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import { Container, Typography } from '@mui/material';

import useBoardStore from '../../store/boardStore';
import { subscribeToBoardSocket } from '../../services/boardSocket';
import { useBoardViewerIdentity } from '../../hooks/useBoardViewerIdentity';
import BoardPageContent from './BoardPageContent';
import BoardPasswordDialog from './BoardPasswordDialog';
import dotstormingLogoCompact from '../../../assets/dotstorming-logo-compact.json';

const BoardPage = () => {
  const { boardId } = useParams<{ boardId: string }>();
  const location = useLocation();
  const setBoardId = useBoardStore((state) => state.setBoardId);
  const loadBoard = useBoardStore((state) => state.loadBoard);
  const applySocketEvent = useBoardStore((state) => state.applySocketEvent);
  const boardDetails = useBoardStore((state) => state.boardDetails);
  const isLoadingBoard = useBoardStore((state) => state.isLoadingBoard);
  const boardLoadError = useBoardStore((state) => state.boardLoadError);
  const { resolveBoardViewerSession } = useBoardViewerIdentity();
  const [resolvedAuthToken, setResolvedAuthToken] = useState<string | undefined>(undefined);
  const [sessionReady, setSessionReady] = useState(false);
  const inFlightLoadKeysRef = useRef<Set<string>>(new Set());

  const handleLoadBoard = useCallback(
    async (selectedBoardId: string, authToken?: string) => {
      const loadKey = `${selectedBoardId}:${authToken ?? ''}`;
      if (inFlightLoadKeysRef.current.has(loadKey)) {
        return;
      }

      inFlightLoadKeysRef.current.add(loadKey);
      try {
        await loadBoard(selectedBoardId, { authToken });
      } finally {
        inFlightLoadKeysRef.current.delete(loadKey);
      }
    },
    [loadBoard]
  );

  useEffect(() => {
    if (!boardId) {
      return;
    }

    setBoardId(boardId);
  }, [boardId, setBoardId]);

  useEffect(() => {
    if (!boardId) {
      return;
    }

    setSessionReady(false);

    void (async () => {
      const { authToken } = await resolveBoardViewerSession(location.search);
      setResolvedAuthToken(authToken);
      setSessionReady(true);
    })();

  }, [boardId, location.search]);

  useEffect(() => {
    if (!boardId || boardDetails?.id !== boardId) return;
    return subscribeToBoardSocket(boardId, (event) => {
      void applySocketEvent(event);
    });
  }, [applySocketEvent, boardId, boardDetails?.id]);

  let pageContent = boardDetails ? <BoardPageContent boardDetails={boardDetails} /> : null;

  if (isLoadingBoard) {
    pageContent = (
      <Container
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Lottie
          animationData={dotstormingLogoCompact}
          loop
          autoplay
          style={{ width: 100, height: 100 }}
          aria-label="Loading board details"
        />
      </Container>
    );
  } else if (!boardDetails && boardLoadError) {
    pageContent = (
      <Container sx={{ pt: 12, pb: 2 }}>
        <Typography variant="h4" align="center">
          {boardLoadError}
        </Typography>
      </Container>
    );
  }

  return (
    <>
      {pageContent}
      <BoardPasswordDialog
        boardId={boardId ?? null}
        authToken={resolvedAuthToken}
        sessionReady={sessionReady}
        onLoadBoard={handleLoadBoard}
      />
    </>
  );
};

export default BoardPage;
